import { useEffect, useState, useCallback, useRef } from 'react';
import { formatCurrency } from '../../utils/currency';
import { oversizeMessage, uploadErrorMessage } from '../../utils/audioUpload';
import { Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import api from '../../services/api';
import ContestantHeader from '../../components/ContestantHeader';
import PaymentForm from '../../components/PaymentForm';
import Tooltip from '../../components/Tooltip';
import './contestant.css';

// Load Stripe outside component to avoid re-creating on every render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const AMP_OPTIONS_DEFAULT = ['MIC', 'DI', 'MIC and DI'];
const AMP_OPTIONS_DRUMS   = ['Acoustic', 'Electronic'];

let _uid = 0;
const uid = () => `inst_${++_uid}`;

// Small numbers read better as words in the category entry-limit warning.
// Falls back to the numeral for anything larger.
const NUMBER_WORDS = [
  'zero', 'one', 'two', 'three', 'four', 'five',
  'six', 'seven', 'eight', 'nine', 'ten',
];
const numberWord = (n) => NUMBER_WORDS[n] ?? String(n);

const EMPTY_SONG = () => ({
  title: '', category_id: '', artist: '', lyrics_text: '', audio_url: '',
  num_performers: '',
  num_vocal_mics: '',
  instruments:    [{ id: uid(), instrument_id: '', amp_type: '', other_text: '' }],
  draftId:    null,   // song.id once the draft has been saved to the DB
  saveStatus: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ContestantSubmit() {
  const [searchParams] = useSearchParams();

  // ── Async state ────────────────────────────────────────────
  const [categories, setCategories]         = useState([]);
  const [instrumentsList, setInstrumentsList] = useState([]);
  const [settings, setSettings]             = useState(null);
  const [existingCount, setExistingCount]   = useState(0);
  const [profileExists, setProfileExists]   = useState(false);
  const [loadingPage, setLoadingPage]       = useState(true);
  const [pageError, setPageError]           = useState('');

  // ── Form state ─────────────────────────────────────────────
  const [songs, setSongs]             = useState([EMPTY_SONG()]);
  const [isMember, setIsMember]       = useState('non-member');
  const [notReleased, setNotReleased] = useState(false);
  const [errors, setErrors]           = useState({});
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting]   = useState(false);

  // ── Audio upload state (per-song-slot) ─────────────────────
  const [uploadingIdx,  setUploadingIdx]  = useState(null);
  const [uploadErrors,  setUploadErrors]  = useState({});
  const [draggingIdx,   setDraggingIdx]   = useState(null); // index of drop-zone being hovered

  // ── Multi-step flow ────────────────────────────────────────
  const [step, setStep]                       = useState('form');
  const [clientSecret, setClientSecret]       = useState('');
  const [paymentSongIds, setPaymentSongIds]   = useState([]);
  const [paymentSongs, setPaymentSongs]       = useState([]);
  const [paymentAmount, setPaymentAmount]     = useState(0);
  const [paymentFeePerSong, setPaymentFeePerSong] = useState(0);
  const [paymentIsMember, setPaymentIsMember] = useState(false);

  // ── Auto-save refs ─────────────────────────────────────────
  // latestSongsRef always holds the current songs array so the async
  // doAutoSave callback reads fresh values after React state updates.
  const latestSongsRef = useRef([]);
  const debounceTimers = useRef({}); // { [draftId]: timeoutId }

  // ── Load initial data ──────────────────────────────────────
  useEffect(() => {
    const draftIdParam = searchParams.get('draftId');

    Promise.all([
      api.get('/api/songs/categories'),
      api.get('/api/contestants/settings'),
      // GET /me returns { contestant: null, user_is_member: bool } for users
      // who haven't submitted yet — no longer a 404, but keep as fallback.
      api.get('/api/contestants/me').catch((e) => {
        if (e.response?.status === 404) return { data: { contestant: null, user_is_member: false } };
        return Promise.reject(e);
      }),
      api.get('/api/songs/instruments').catch(() => ({ data: { instruments: [] } })),
    ])
      .then(async ([catRes, setRes, cRes, instRes]) => {
        setCategories(catRes.data.categories ?? []);
        setSettings(setRes.data.settings);
        setInstrumentsList(instRes.data.instruments ?? []);

        const contestantData = cRes?.data?.contestant ?? null;

        if (contestantData) {
          setProfileExists(true);
          // Drafts do not count toward the submission limit
          const activeSongs = (contestantData.songs ?? []).filter(
            (s) => s.status !== 'withdrawn' && s.status !== 'draft'
          );
          setExistingCount(activeSongs.length);
          if (contestantData.is_member) setIsMember('member');

          // Load an existing draft when "Continue editing" link is used
          if (draftIdParam) {
            try {
              const res = await api.get(`/api/songs/${draftIdParam}`);
              const draftSong = res.data.song;
              const initialSong = {
                title:       draftSong.title       || '',
                category_id: draftSong.category_id ? String(draftSong.category_id) : '',
                artist:      draftSong.artist      || '',
                lyrics_text: draftSong.lyrics_text || '',
                audio_url:   draftSong.audio_url   || '',
                num_performers: draftSong.num_performers ?? '',
                num_vocal_mics: draftSong.num_vocal_mics ?? '',
                instruments: Array.isArray(draftSong.instruments) && draftSong.instruments.length > 0
                  ? draftSong.instruments.map((inst) => ({
                      id:            inst.id || uid(),
                      instrument_id: inst.instrument_id ? String(inst.instrument_id) : '',
                      amp_type:      inst.amp_type      || '',
                      other_text:    inst.other_text    || '',
                    }))
                  : [{ id: uid(), instrument_id: '', amp_type: '', other_text: '' }],
                draftId:     draftSong.id,
                saveStatus:  'idle',
              };
              setSongs([initialSong]);
              latestSongsRef.current = [initialSong];
            } catch {
              // Draft load failed — fall back to empty form (draft will be
              // created again when the user types a title and clicks away)
              const fallback = EMPTY_SONG();
              setSongs([fallback]);
              latestSongsRef.current = [fallback];
            }
          } else {
            // Fresh form open — start with empty song; draft is created the
            // first time the user fills in a title and moves focus away.
            const fallback = EMPTY_SONG();
            setSongs([fallback]);
            latestSongsRef.current = [fallback];
          }
        } else {
          // New contestant with no profile yet — no draft until profile exists.
          // Initialise isMember from users.is_member (sent in /me response) so
          // the fee preview shows the correct rate without needing a radio.
          setProfileExists(false);
          setExistingCount(0);
          if (cRes?.data?.user_is_member) setIsMember('member');
          const fallback = EMPTY_SONG();
          setSongs([fallback]);
          latestSongsRef.current = [fallback];
        }
      })
      .catch(() => setPageError('Failed to load submission form. Please try refreshing.'))
      .finally(() => setLoadingPage(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-save logic ────────────────────────────────────────

  /** Save the current field values of a draft song to the server. */
  const doAutoSave = useCallback(async (draftId) => {
    const song = latestSongsRef.current.find((s) => s.draftId === draftId);
    if (!song) return;

    setSongs((prev) =>
      prev.map((s) => s.draftId === draftId ? { ...s, saveStatus: 'saving' } : s)
    );
    try {
      await api.put(`/api/songs/${draftId}/draft`, {
        title:          song.title       || null,
        category_id:    song.category_id ? Number(song.category_id) : null,
        artist:         song.artist      || null,
        lyrics_text:    song.lyrics_text || null,
        audio_url:      song.audio_url   || null,
        num_performers: song.num_performers !== '' ? Number(song.num_performers) : null,
        num_vocal_mics: song.num_vocal_mics !== '' ? Number(song.num_vocal_mics) : null,
        instruments:    song.instruments?.filter((inst) => inst.instrument_id).length > 0
          ? song.instruments.filter((inst) => inst.instrument_id).map((inst) => ({
              id:            inst.id,
              instrument_id: Number(inst.instrument_id),
              amp_type:      inst.amp_type  || null,
              other_text:    inst.other_text || null,
            }))
          : null,
      });
      setSongs((prev) =>
        prev.map((s) => s.draftId === draftId ? { ...s, saveStatus: 'saved' } : s)
      );
    } catch {
      setSongs((prev) =>
        prev.map((s) => s.draftId === draftId ? { ...s, saveStatus: 'error' } : s)
      );
    }
  }, []);

  /**
   * Called when the title field loses focus (onBlur).
   * If the field has content and no draft exists yet, creates one in the DB
   * so subsequent field changes have a record to auto-save against.
   */
  const createDraftOnTitleBlur = useCallback(async (index) => {
    const song = latestSongsRef.current[index];
    if (!song || !song.title.trim() || song.draftId) return; // nothing to do

    try {
      const res = await api.post('/api/songs/draft', { title: song.title.trim() });
      const newDraftId = res.data.song.id;
      setSongs((prev) => {
        const next = prev.map((s, i) =>
          i === index ? { ...s, draftId: newDraftId, saveStatus: 'saved' } : s
        );
        latestSongsRef.current = next;
        return next;
      });
      // Immediately save any other fields already filled in
      clearTimeout(debounceTimers.current[newDraftId]);
      debounceTimers.current[newDraftId] = setTimeout(() => doAutoSave(newDraftId), 200);
    } catch {
      // Draft creation failed silently — form still usable, just no auto-save
    }
  }, [doAutoSave]);

  // ── Song entry helpers ─────────────────────────────────────

  const updateSong = useCallback((index, field, value) => {
    setSongs((prev) => {
      const next = prev.map((s, i) => (i === index ? { ...s, [field]: value } : s));
      latestSongsRef.current = next;
      // Debounce: schedule auto-save 2 s after the last change
      const draftId = next[index]?.draftId;
      if (draftId) {
        clearTimeout(debounceTimers.current[draftId]);
        debounceTimers.current[draftId] = setTimeout(() => doAutoSave(draftId), 2000);
      }
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`song_${index}_${field}`];
      return next;
    });
  }, [doAutoSave]);

  const addSong = useCallback(() => {
    // Add a new empty slot; a draft will be created when the user types a
    // title and clicks away (onBlur on the title field).
    setSongs((prev) => {
      const next = [...prev, EMPTY_SONG()];
      latestSongsRef.current = next;
      return next;
    });
  }, []);

  const removeSong = useCallback(async (index) => {
    const song = latestSongsRef.current[index];
    if (song?.draftId) {
      // Cancel any pending debounce and delete the draft from the DB
      clearTimeout(debounceTimers.current[song.draftId]);
      delete debounceTimers.current[song.draftId];
      api.delete(`/api/songs/${song.draftId}`).catch(() => { /* non-fatal */ });
    }
    setSongs((prev) => {
      const next = prev.filter((_, i) => i !== index);
      latestSongsRef.current = next;
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`song_${index}_`)) delete next[k];
      });
      return next;
    });
    setUploadErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  }, []);

  // ── Audio file upload handler ──────────────────────────────
  const handleAudioUpload = useCallback(async (index, file) => {
    if (!file) return;

    // Reject oversized files locally — no point starting an upload that nginx
    // will refuse, and the contestant gets told why immediately.
    const tooBig = oversizeMessage(file);
    if (tooBig) {
      setUploadErrors((prev) => ({ ...prev, [index]: tooBig }));
      return;
    }

    setUploadingIdx(index);
    setUploadErrors((prev) => ({ ...prev, [index]: '' }));
    try {
      const formData = new FormData();
      formData.append('audio', file);
      const res = await api.post('/api/songs/upload-audio', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // updateSong triggers auto-save debounce automatically
      updateSong(index, 'audio_url', res.data.audio_url);
    } catch (err) {
      setUploadErrors((prev) => ({ ...prev, [index]: uploadErrorMessage(err) }));
    } finally {
      setUploadingIdx(null);
    }
  }, [updateSong]);

  // ── Validation ─────────────────────────────────────────────

  function validate() {
    const errs = {};
    songs.forEach((s, i) => {
      if (!s.title.trim())  errs[`song_${i}_title`]       = 'Song title is required.';
      if (!s.category_id)   errs[`song_${i}_category_id`] = 'Please select a category.';
      if (!s.artist.trim()) errs[`song_${i}_artist`]       = 'Artist / composer name is required.';
      const cat = categories.find((c) => String(c.id) === String(s.category_id));
      if (cat?.requires_music_file && !s.audio_url) {
        errs[`song_${i}_audio_url`] = 'An audio file is required for this category.';
      }
      if (cat?.requires_lyrics_text && !s.lyrics_text.trim()) {
        errs[`song_${i}_lyrics_text`] = 'Lyrics are required for this category.';
      }
      if (cat?.is_live) {
        s.instruments.forEach((inst, j) => {
          const instName = instrumentsList.find((x) => String(x.id) === String(inst.instrument_id))?.name;
          if (instName === 'Other' && !inst.other_text?.trim()) {
            errs[`song_${i}_instrument_${j}_other_text`] = 'Please specify the instrument.';
          }
        });
      }
    });
    if (!notReleased) {
      errs['notReleased'] = 'You must confirm that this song has not been previously released.';
    }
    const maxSlots   = settings?.max_songs_per_contestant ?? 3;
    const totalAfter = existingCount + songs.length;
    if (totalAfter > maxSlots) {
      errs['_limit'] = `You can only submit ${maxSlots - existingCount} more song(s). Remove some entries.`;
    }
    return errs;
  }

  // ── "Proceed to Payment" handler ───────────────────────────

  const handleProceedToPayment = async (e) => {
    e.preventDefault();
    setServerError('');

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Cancel all pending auto-saves — the submit call below will persist the
    // final values atomically.
    songs.forEach((s) => {
      if (s.draftId) {
        clearTimeout(debounceTimers.current[s.draftId]);
        delete debounceTimers.current[s.draftId];
      }
    });

    setSubmitting(true);
    try {
      // Step 1: Create contestant profile if this is their first submission.
      // is_member is now read server-side from users.is_member — not sent here.
      if (!profileExists) {
        await api.post('/api/contestants/register', {});
      }

      // Step 2: Submit songs — draft_id tells the backend to UPDATE the existing
      // draft record rather than INSERT a new one.
      const payload = songs.map((s) => ({
        title:          s.title.trim(),
        category_id:    Number(s.category_id),
        artist:         s.artist.trim()      || undefined,
        lyrics_text:    s.lyrics_text.trim() || undefined,
        audio_url:      s.audio_url          || undefined,
        draft_id:       s.draftId            || undefined,
        num_performers: s.num_performers !== '' ? Number(s.num_performers) : undefined,
        num_vocal_mics: s.num_vocal_mics !== '' ? Number(s.num_vocal_mics) : undefined,
        instruments:    s.instruments?.filter((inst) => inst.instrument_id).length > 0
          ? s.instruments.filter((inst) => inst.instrument_id).map((inst) => ({
              id:              inst.id,
              instrument_id:   Number(inst.instrument_id),
              instrument_name: instrumentsList.find((x) => String(x.id) === String(inst.instrument_id))?.name || '',
              amp_type:        inst.amp_type  || null,
              other_text:      inst.other_text || null,
            }))
          : undefined,
      }));

      const songRes = await api.post('/api/songs/submit', payload);
      const submittedSongs = songRes.data.songs;
      const songIds = submittedSongs.map((s) => s.id);

      // Step 3: Create Stripe PaymentIntent
      const intentRes = await api.post('/api/payments/create-intent', {
        song_ids: songIds,
      });

      const {
        client_secret,
        amount,
        fee_per_song,
        is_member: memberStatus,
      } = intentRes.data;

      setClientSecret(client_secret);
      setPaymentSongIds(songIds);
      setPaymentSongs(submittedSongs.map((s) => ({ id: s.id, title: s.title })));
      setPaymentAmount(amount);
      setPaymentFeePerSong(fee_per_song);
      setPaymentIsMember(memberStatus);

      setStep('payment');
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      const msg =
        err.response?.data?.error ??
        err.response?.data?.errors?.join(' ') ??
        'Something went wrong. Please try again.';
      setServerError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Payment success handler ────────────────────────────────

  const handlePaymentSuccess = async (stripePaymentIntentId) => {
    try {
      await api.post('/api/payments/confirm', {
        payment_intent_id: stripePaymentIntentId,
        song_ids:          paymentSongIds,
      });
      setStep('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.warn('Payment confirm endpoint error (webhook will handle):', err.message);
      setStep('success');
    }
  };

  // ── Derived values ─────────────────────────────────────────

  const maxSlots   = settings?.max_songs_per_contestant ?? 3;
  const regOpen    = settings?.registration_open ?? false;
  const slotsLeft  = Math.max(0, maxSlots - existingCount);
  const canAddMore = songs.length < slotsLeft;

  const feePerSong = settings
    ? (isMember === 'member' ? settings.member_fee_cents : settings.non_member_fee_cents)
    : 0;
  const totalFeePreview = feePerSong * songs.length;

  // Global save status: aggregated across all song slots
  const hasDraft = songs.some((s) => s.draftId);
  const globalSaveStatus = !hasDraft ? 'idle'
    : songs.some((s) => s.saveStatus === 'saving') ? 'saving'
    : songs.some((s) => s.saveStatus === 'error')  ? 'error'
    : songs.some((s) => s.saveStatus === 'saved')  ? 'saved'
    : 'idle';

  // ── Render: loading ────────────────────────────────────────
  if (loadingPage) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-loading">
            <div className="c-spinner" aria-label="Loading…" />
            <p>Loading submission form…</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: page error ─────────────────────────────────────
  if (pageError) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert" role="alert">{pageError}</div>
        </main>
      </div>
    );
  }

  // ── Render: registration closed ────────────────────────────
  if (!regOpen && !searchParams.get('draftId')) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert c-alert--info" role="status">
            <strong>Submissions are currently closed.</strong>
            <br />
            Song submission is not open at this time. Please check back later.
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/contestant/dashboard" className="c-btn">Return to Dashboard</Link>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: at limit ───────────────────────────────────────
  if (slotsLeft === 0 && !searchParams.get('draftId')) {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
          <div className="c-alert c-alert--warning" role="status">
            <strong>Submission limit reached.</strong>
            <br />
            You have already submitted the maximum of {maxSlots} song(s).
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <Link to="/contestant/dashboard" className="c-btn">View My Dashboard</Link>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: success ────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <div className="c-success">
            <div className="c-success__icon">🎉</div>
            <h2 className="c-success__title">
              Payment Complete!
            </h2>
            <p className="c-success__text">
              Your {paymentSongs.length > 1 ? `${paymentSongs.length} songs have` : 'song has'} been
              submitted and your payment of{' '}
              <strong>{formatCurrency(paymentAmount)}</strong> was successful.
              We'll be in touch with next steps.
            </p>
            <Link to="/contestant/dashboard" className="c-btn">
              View My Dashboard →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // ── Render: payment step ───────────────────────────────────
  if (step === 'payment') {
    return (
      <div className="c-page">
        <ContestantHeader />
        <main className="c-main">
          <p className="c-payment-heading">Step 2 of 2 — Pay Entry Fee</p>
          <p className="c-payment-subheading">
            Your {paymentSongs.length > 1 ? 'songs have' : 'song has'} been registered.
            Complete payment to confirm your entry.
          </p>

          <Elements stripe={stripePromise}>
            <PaymentForm
              clientSecret={clientSecret}
              songs={paymentSongs}
              amount={paymentAmount}
              feePerSong={paymentFeePerSong}
              isMember={paymentIsMember}
              onSuccess={handlePaymentSuccess}
            />
          </Elements>

          <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8rem', color: '#9ca3af' }}>
            Your songs are saved — you can also pay later from your{' '}
            <Link to="/contestant/dashboard" style={{ color: '#1a1a2e' }}>dashboard</Link>.
          </p>
        </main>
      </div>
    );
  }

  // ── Render: song entry form (step = 'form') ────────────────
  return (
    <div className="c-page">
      <ContestantHeader />

      <main className="c-main">
        <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>

        <div className="c-section-title">Submit a Song — Step 1 of 2</div>

        <p style={{ fontSize: '0.9rem', margin: '0.25rem 0 1rem' }}>
          <a
            href="/assets/SCALA_FOOM_Rules_2026_SM.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#d4af37' }}
          >
            📄 Download the FOOM 2026 Competition Rules (PDF)
          </a>
        </p>

        {/* ── Auto-save status indicator ── */}
        {globalSaveStatus !== 'idle' && (
          <div className={`c-save-status c-save-status--${globalSaveStatus}`} aria-live="polite">
            {globalSaveStatus === 'saving' && 'Saving…'}
            {globalSaveStatus === 'saved'  && 'Saved ✓'}
            {globalSaveStatus === 'error'  && 'Save failed — please check your connection'}
          </div>
        )}

        {existingCount > 0 && (
          <div className="c-alert c-alert--info" role="status">
            You have already submitted {existingCount} of {maxSlots} song(s).
            You can add up to {slotsLeft} more.
          </div>
        )}

        {errors['_limit'] && (
          <div className="c-alert" role="alert">{errors['_limit']}</div>
        )}
        {serverError && (
          <div className="c-alert" role="alert">{serverError}</div>
        )}

        <form onSubmit={handleProceedToPayment} noValidate>

          {/* ── Song entries ── */}
          {songs.map((song, i) => (
            <div key={song.draftId ?? i} className="c-song-entry">
              <div className="c-song-entry__header">
                <h3 className="c-song-entry__label">
                  Song {songs.length > 1 ? i + 1 : ''}
                </h3>
                {songs.length > 1 && (
                  <button
                    type="button"
                    className="c-remove-btn"
                    onClick={() => removeSong(i)}
                    aria-label={`Remove song ${i + 1}`}
                  >
                    ✕ Remove
                  </button>
                )}
              </div>

              <div className="c-form-group">
                <label className="c-label" htmlFor={`title-${i}`}>
                  Song title <span className="c-label__required">*</span>
                  <Tooltip tooltipKey="song_title" />
                </label>
                <input
                  id={`title-${i}`}
                  type="text"
                  className={`c-input${errors[`song_${i}_title`] ? ' is-error' : ''}`}
                  value={song.title}
                  onChange={(e) => updateSong(i, 'title', e.target.value)}
                  onBlur={() => createDraftOnTitleBlur(i)}
                  placeholder="Enter the song title"
                />
                {errors[`song_${i}_title`] && (
                  <p className="c-field-error" role="alert">{errors[`song_${i}_title`]}</p>
                )}
              </div>

              <div className="c-form-group">
                <label className="c-label" htmlFor={`category-${i}`}>
                  Category <span className="c-label__required">*</span>
                  <Tooltip tooltipKey="song_category" />
                  {/* Warn up-front when the chosen category caps entries, so a
                      contestant doesn't fill the whole form and only find out
                      on submit. Driven by the category's configured limit. */}
                  {(() => {
                    const cat = categories.find(
                      (c) => String(c.id) === String(song.category_id)
                    );
                    const max = cat?.max_entries_per_contestant;
                    if (!max) return null;
                    return (
                      <span className="c-category-limit-note">
                        {` Maximum of ${numberWord(max)} entr${max === 1 ? 'y' : 'ies'} per contestant in the ${cat.name.toUpperCase()} category`}
                      </span>
                    );
                  })()}
                </label>
                <select
                  id={`category-${i}`}
                  className={`c-select${errors[`song_${i}_category_id`] ? ' is-error' : ''}`}
                  value={song.category_id}
                  onChange={(e) => updateSong(i, 'category_id', e.target.value)}
                >
                  <option value="">— Select a category —</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                {errors[`song_${i}_category_id`] && (
                  <p className="c-field-error" role="alert">{errors[`song_${i}_category_id`]}</p>
                )}
              </div>

              <div className="c-form-group">
                <label className="c-label" htmlFor={`artist-${i}`}>
                  Artist / composer <span className="c-label__required">*</span>
                </label>
                <input
                  id={`artist-${i}`}
                  type="text"
                  className={`c-input${errors[`song_${i}_artist`] ? ' is-error' : ''}`}
                  value={song.artist}
                  onChange={(e) => updateSong(i, 'artist', e.target.value)}
                  placeholder="Composer or performing artist name"
                />
                {errors[`song_${i}_artist`] && (
                  <p className="c-field-error" role="alert">{errors[`song_${i}_artist`]}</p>
                )}
              </div>

              {/* ── Conditional: Music file (if category requires it) ── */}
              {(() => {
                const cat = categories.find((c) => String(c.id) === String(song.category_id));
                if (!cat?.requires_music_file) return null;
                const isUploading = uploadingIdx === i;
                const uploadError = uploadErrors[i];
                const audioUrl    = song.audio_url;
                return (
                  <div className="c-form-group">
                    <label className="c-label" htmlFor={`audio-${i}`}>
                      Music file{' '}
                      <span className="c-label-optional">(MP3, WAV, M4A — max 50 MB)</span>
                      <Tooltip tooltipKey="song_music_file" />
                    </label>

                    {audioUrl && !isUploading && (
                      <div className="c-file-success">
                        <span className="c-file-success__icon">🎵</span>
                        <span className="c-file-success__name">
                          {audioUrl.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          className="c-file-remove-btn"
                          onClick={() => updateSong(i, 'audio_url', '')}
                          aria-label="Remove audio file"
                        >
                          ✕ Remove
                        </button>
                      </div>
                    )}

                    {isUploading && (
                      <div className="c-file-uploading">
                        <span className="c-spinner c-spinner--sm" aria-hidden="true" />
                        Uploading…
                      </div>
                    )}

                    {!audioUrl && !isUploading && (
                      <div
                        className="c-file-drop"
                        style={draggingIdx === i
                          ? { borderColor: '#d4af37', background: '#fffbeb', borderStyle: 'solid' }
                          : {}}
                        role="button"
                        tabIndex={0}
                        aria-label="Audio file upload — click or drag a file here"
                        onClick={() => document.getElementById(`audio-${i}`).click()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') document.getElementById(`audio-${i}`).click();
                        }}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingIdx(i); }}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingIdx(i); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDraggingIdx(null); }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDraggingIdx(null);
                          const file = e.dataTransfer.files[0];
                          if (!file) return;
                          const validExts = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.mp4'];
                          const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
                          if (!file.type.startsWith('audio/') && !validExts.includes(ext)) {
                            setUploadErrors((prev) => ({ ...prev, [i]: 'Please upload an MP3, WAV, M4A, AAC, OGG, or FLAC audio file.' }));
                            return;
                          }
                          if (file.size > 50 * 1024 * 1024) {
                            setUploadErrors((prev) => ({ ...prev, [i]: 'File too large — maximum size is 50 MB.' }));
                            return;
                          }
                          handleAudioUpload(i, file);
                        }}
                      >
                        <input
                          id={`audio-${i}`}
                          type="file"
                          accept=".mp3,.wav,.m4a,.aac,.ogg,.flac,.mp4,audio/*"
                          className="c-file-input"
                          onChange={(e) => handleAudioUpload(i, e.target.files[0])}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="c-file-drop__icon">
                          {draggingIdx === i ? '📂' : '📎'}
                        </span>
                        <span className="c-file-drop__text">
                          {draggingIdx === i ? 'Drop to upload' : 'Click to choose a file or drag it here'}
                        </span>
                        <span className="c-file-drop__hint">
                          MP3, WAV, M4A, AAC, OGG, FLAC — max 50 MB
                        </span>
                      </div>
                    )}

                    {uploadError && (
                      <p className="c-field-error" role="alert">{uploadError}</p>
                    )}
                    {errors[`song_${i}_audio_url`] && (
                      <p className="c-field-error" role="alert">{errors[`song_${i}_audio_url`]}</p>
                    )}
                  </div>
                );
              })()}

              {/* ── Conditional: Lyrics text (if category requires it) ── */}
              {(() => {
                const cat = categories.find((c) => String(c.id) === String(song.category_id));
                if (!cat?.requires_lyrics_text) return null;
                const instructions = cat.lyrics_instructions ||
                  'Enter your song lyrics below. Please do not include any chord symbols or other annotation - lyrics only.';
                const placeholder = cat.lyrics_placeholder ||
                  'Your lyrics here. Remember, words only.';
                return (
                  <div className="c-form-group">
                    <label className="c-label" htmlFor={`lyrics-${i}`}>
                      Lyrics <span className="c-label__required">*</span>
                      <Tooltip tooltipKey="song_lyrics_text" />
                    </label>
                    <p className="c-field-hint">{instructions}</p>
                    <textarea
                      id={`lyrics-${i}`}
                      className={`c-textarea${errors[`song_${i}_lyrics_text`] ? ' is-error' : ''}`}
                      rows={10}
                      value={song.lyrics_text}
                      onChange={(e) => updateSong(i, 'lyrics_text', e.target.value)}
                      placeholder={placeholder}
                    />
                    {errors[`song_${i}_lyrics_text`] && (
                      <p className="c-field-error" role="alert">{errors[`song_${i}_lyrics_text`]}</p>
                    )}
                  </div>
                );
              })()}

              {/* ── Conditional: Performance Requirements (if category is live) ── */}
              {(() => {
                const cat = categories.find((c) => String(c.id) === String(song.category_id));
                if (!cat?.is_live) return null;
                const maxPerf = settings?.max_performers ?? 10;
                return (
                  <div className="c-form-group" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.25rem', marginTop: '0.5rem' }}>
                    <h4 className="c-label" style={{ fontSize: '1rem', marginBottom: '0.35rem' }}>
                      Performance Requirements
                    </h4>
                    <p className="c-field-hint" style={{ marginBottom: '1rem' }}>
                      Please provide your stage requirements for this live performance.
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
                      <div>
                        <label className="c-label" htmlFor={`num-performers-${i}`}>
                          Number of performers <span className="c-label__required">*</span>
                        </label>
                        <input
                          id={`num-performers-${i}`}
                          type="number"
                          min={1}
                          max={maxPerf}
                          className="c-input"
                          value={song.num_performers}
                          onChange={(e) => updateSong(i, 'num_performers', e.target.value)}
                          placeholder={`1–${maxPerf}`}
                        />
                      </div>
                      <div>
                        <label className="c-label" htmlFor={`num-vocal-mics-${i}`}>
                          Vocal microphones needed
                        </label>
                        <input
                          id={`num-vocal-mics-${i}`}
                          type="number"
                          min={0}
                          className="c-input"
                          value={song.num_vocal_mics}
                          onChange={(e) => updateSong(i, 'num_vocal_mics', e.target.value)}
                          placeholder="0"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="c-label" style={{ marginBottom: '0.5rem' }}>
                        Instruments requiring mic or DI
                      </label>
                      {song.instruments.map((inst, j) => {
                        const selectedInst = instrumentsList.find((x) => String(x.id) === String(inst.instrument_id));
                        const isDrums      = selectedInst?.name === 'Drums';
                        const isOther      = selectedInst?.name === 'Other';
                        const ampOptions   = isDrums ? AMP_OPTIONS_DRUMS : AMP_OPTIONS_DEFAULT;
                        return (
                          <div key={inst.id} style={{ marginBottom: '0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              {/* Instrument dropdown */}
                              <select
                                className="c-select"
                                style={{ minWidth: '180px', flex: '1 1 180px' }}
                                value={inst.instrument_id}
                                onChange={(e) => {
                                  const newId = e.target.value;
                                  const newInst = instrumentsList.find((x) => String(x.id) === newId);
                                  const updated = song.instruments.map((x, k) =>
                                    k === j ? {
                                      ...x,
                                      instrument_id: newId,
                                      amp_type:      '',
                                      other_text:    newInst?.name !== 'Other' ? '' : x.other_text,
                                    } : x
                                  );
                                  updateSong(i, 'instruments', updated);
                                }}
                              >
                                <option value="">— Select instrument —</option>
                                {instrumentsList.map((t) => (
                                  <option key={t.id} value={String(t.id)}>{t.name}</option>
                                ))}
                              </select>

                              {/* Amplification type dropdown — only when instrument selected */}
                              {inst.instrument_id && (
                                <select
                                  className="c-select"
                                  style={{ minWidth: '150px', flex: '1 1 150px' }}
                                  value={inst.amp_type}
                                  onChange={(e) => {
                                    const updated = song.instruments.map((x, k) =>
                                      k === j ? { ...x, amp_type: e.target.value } : x
                                    );
                                    updateSong(i, 'instruments', updated);
                                  }}
                                >
                                  <option value="">— Amplification —</option>
                                  {ampOptions.map((opt) => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              )}

                              {/* Remove button */}
                              {song.instruments.length > 1 && (
                                <button
                                  type="button"
                                  className="c-remove-btn"
                                  onClick={() => {
                                    const updated = song.instruments.filter((_, k) => k !== j);
                                    updateSong(i, 'instruments', updated);
                                  }}
                                  aria-label="Remove instrument"
                                >
                                  ✕
                                </button>
                              )}
                            </div>

                            {/* "Other" free-text field */}
                            {isOther && (
                              <div style={{ marginTop: '0.4rem' }}>
                                <input
                                  type="text"
                                  className={`c-input${errors[`song_${i}_instrument_${j}_other_text`] ? ' is-error' : ''}`}
                                  placeholder="Please specify instrument"
                                  value={inst.other_text || ''}
                                  onChange={(e) => {
                                    const updated = song.instruments.map((x, k) =>
                                      k === j ? { ...x, other_text: e.target.value } : x
                                    );
                                    updateSong(i, 'instruments', updated);
                                    setErrors((prev) => {
                                      const next = { ...prev };
                                      delete next[`song_${i}_instrument_${j}_other_text`];
                                      return next;
                                    });
                                  }}
                                />
                                {errors[`song_${i}_instrument_${j}_other_text`] && (
                                  <p className="c-field-error" role="alert">
                                    {errors[`song_${i}_instrument_${j}_other_text`]}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        className="c-add-song-btn"
                        style={{ marginTop: '0.25rem', fontSize: '0.85rem', padding: '0.4rem 0.85rem' }}
                        onClick={() => {
                          const updated = [...song.instruments, { id: uid(), instrument_id: '', amp_type: '', other_text: '' }];
                          updateSong(i, 'instruments', updated);
                        }}
                      >
                        + Add Instrument
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}

          {/* ── Add song / quota note ── */}
          {canAddMore ? (
            <button type="button" className="c-add-song-btn" onClick={addSong}>
              ＋ Add another song
            </button>
          ) : (
            <p className="c-quota-note">
              You have used all {songs.length} remaining slot{songs.length !== 1 ? 's' : ''}.
            </p>
          )}

          {/* ── Fee preview ── */}
          {feePerSong > 0 && (
            <div className="c-payment-summary" style={{ marginTop: '1rem' }}>
              <div className="c-payment-summary__badge">
                {isMember === 'member' ? 'SCALA Member rate' : 'Non-member rate'}
              </div>
              <div className="c-payment-summary__total">
                <span>
                  {songs.length} song{songs.length !== 1 ? 's' : ''} ×{' '}
                  {formatCurrency(feePerSong)}
                </span>
                <span>{formatCurrency(totalFeePreview)}</span>
              </div>
            </div>
          )}

          {/* ── Release confirmation ── */}
          <div className="c-form-group" style={{ marginTop: '1.5rem' }}>
            <label className="c-radio-label" style={{ alignItems: 'flex-start', gap: '0.6rem' }}>
              <input
                type="checkbox"
                checked={notReleased}
                onChange={(e) => {
                  setNotReleased(e.target.checked);
                  if (e.target.checked) {
                    setErrors((prev) => { const next = { ...prev }; delete next['notReleased']; return next; });
                  }
                }}
                style={{ marginTop: '0.2rem', flexShrink: 0 }}
              />
              <span>
                I confirm that this song has not been previously released{' '}
                <span className="c-label__required">*</span>
              </span>
            </label>
            {errors['notReleased'] && (
              <p className="c-field-error" role="alert" style={{ marginTop: '0.35rem' }}>
                {errors['notReleased']}
              </p>
            )}
          </div>

          {/* ── Proceed button ── */}
          <button
            type="submit"
            className="c-submit-btn"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                Setting up payment…
              </>
            ) : (
              `Continue to Payment →`
            )}
          </button>
        </form>

        <div style={{ marginTop: '32px' }}>
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>
      </main>
    </div>
  );
}
